import { useParams, useNavigate } from 'react-router-dom';
import { cn } from '@/components/ui/utils';
import { useSkillDetail } from '../hooks/useSkillDetail';
import { InternBadge } from './shared/Badge';
import { DetailSkeleton } from './shared/InternSkeletons';
import { InternErrorState } from './shared/InternEmptyState';

const statusLabels: Record<string, string> = {
  suggested: 'Suggested',
  exploring: 'Exploring',
  explored:  'Explored',
};

const statusVariant: Record<string, 'pending' | 'active' | 'completed'> = {
  suggested: 'pending',
  exploring: 'active',
  explored:  'completed',
};

export function SkillDetailPage() {
  const { skillId } = useParams<{ skillId: string }>();
  const navigate = useNavigate();
  const { skill, loading, error, saving, updateStatus, updateNotes, refresh } = useSkillDetail(skillId);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-warm-white)' }}>
      {/* Mini top bar */}
      <div className="sticky top-0 z-20 border-b bg-white px-6 py-3 flex items-center gap-4">
        <button
          onClick={() => navigate('/intern/dashboard')}
          className="text-[14px] font-[500] text-[var(--color-forest)] hover:underline flex items-center gap-1"
        >
          ← Today
        </button>
      </div>

      <main className="mx-auto max-w-[880px] px-6 py-10">
        {loading && <DetailSkeleton />}
        {error && <InternErrorState onRetry={refresh} />}

        {!loading && !error && skill && (
          <div className="space-y-8">
            {/* Header */}
            <div className="rounded-[4px] border bg-white overflow-hidden" style={{ borderColor: 'rgba(27,58,45,0.15)' }}>
              {/* Gold top bar */}
              <div className="h-[3px]" style={{ background: 'var(--color-gold)' }} />
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h1
                    className="font-serif font-[600] text-[26px] leading-snug flex-1"
                    style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-near-black)' }}
                  >
                    {skill.title}
                  </h1>
                </div>

                {/* Status control */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {(['suggested', 'exploring', 'explored'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(s)}
                      className={cn(
                        'px-3 py-1.5 rounded-[4px] text-[13px] font-[500] border transition-all duration-200',
                        skill.status === s
                          ? 'bg-[var(--color-forest)] text-[var(--color-warm-white)] border-[var(--color-forest)]'
                          : 'bg-white text-[var(--color-charcoal)] border-[var(--border)] hover:border-[var(--color-forest)]'
                      )}
                      aria-pressed={skill.status === s}
                    >
                      {statusLabels[s]}
                    </button>
                  ))}
                </div>

                {skill.description && (
                  <p className="text-[16px] text-[var(--color-charcoal)] leading-relaxed">
                    {skill.description}
                  </p>
                )}
              </div>
            </div>

            {/* Links */}
            {skill.links && skill.links.length > 0 && (
              <div>
                <h2 className="text-[16px] font-[600] text-[var(--color-near-black)] mb-3">References & links</h2>
                <div className="flex flex-wrap gap-2">
                  {skill.links.map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[4px] text-[13px] font-[500] border bg-white border-[var(--border)] text-[var(--color-forest)] hover:underline transition-colors"
                    >
                      {link.label} ↗
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Notes — autosave */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-[16px] font-[600] text-[var(--color-near-black)]">Your notes</h2>
                {saving && (
                  <span className="text-[12px] text-[var(--color-mid-gray)]" aria-live="polite">
                    Saving…
                  </span>
                )}
              </div>
              <p className="text-[13px] text-[var(--color-mid-gray)] mb-3">
                What did you try? What did you conclude? What would you recommend?
              </p>
              <textarea
                value={skill.notes ?? ''}
                onChange={(e) => updateNotes(e.target.value)}
                placeholder="Start writing your findings here…"
                rows={8}
                className={cn(
                  'w-full rounded-[4px] border px-4 py-3 text-[16px] leading-relaxed resize-y',
                  'border-[var(--color-charcoal)]/30 bg-white text-[var(--color-near-black)]',
                  'placeholder:text-[var(--color-mid-gray)]',
                  'focus:outline-none focus:ring-2 focus:ring-[var(--color-forest)] focus:ring-offset-0',
                  'transition-colors duration-200 min-h-[180px]'
                )}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
